<template>
    <div class="dialog" id="add-furniture-dialog" aria-hidden="true">
        <div class="dialog-overlay" tabindex="-1" data-a11y-dialog-hide></div>
        <div
            role="dialog"
            class="dialog-content dialog-content--furniture"
            aria-labelledby="my-dialog-title"
            aria-describedby="my-dialog-description"
        >
            <button
                data-a11y-dialog-hide
                class="dialog-close"
                aria-label="Close this dialog window"
            >
                <img src="@/assets/icons/dialog-close.svg" width="24" height="24" :alt="$t('project_settings.add_furniture_modal.dialog_close')" />
            </button>

            <div class="project-add loader-block-parent">
                <div v-show="isLoading" class="loader-block">
                    <div>
                        <div class="lds-ring"><div></div><div></div><div></div><div></div></div>
                    </div>
                </div>
                <p class="project-add__heading">{{ $t('project_settings.add_furniture_modal.add_furnitures') }}</p>
                <div class="furniture__list">
                    <div class="furniture-item"
                         v-for="(furniture, index) in furnitures"
                         :class="{'furniture-item--selected': selectedFurnituresIds.includes(furniture.id)}"
                         @click.prevent="toggleFurniture(furniture.id)"
                    >
                        <div class="furniture-item__checked"></div>
                        <div class="furniture-item__top">
                            <img :src="furniture.imagePath" :alt="furniture.name_pl ?? furniture.name" />
                        </div>
                        <div class="furniture-item__bottom">
                            <div>{{ furniture.name_pl ?? furniture.name }}</div>
                            <div class="control control--input control-number">
                                <div>
                                    <button type="button" data-type="dec" @click.stop="decrementAmount(furniture.id)">-</button>
                                    <input class="amount-input" placeholder="1" v-model="furnitureAmounts[furniture.id]" type="text" />
                                    <button type="button" data-type="inc" @click.stop="incrementAmount(furniture.id)">+</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="dialog__buttons">
                    <button v-show="!moreFurnituresLoaded"
                            class="dialog__button"
                            @click.prevent="loadMoreFurnitures"
                            :class="{'disabled': sendingForm}"
                            :disabled="sendingForm"
                    >{{ $t('project_settings.add_furniture_modal.more') }}</button>
                    <button class="dialog__button dialog__button--alt"
                            @click.prevent="submitAddFurnitures"
                            :class="{'disabled': sendingForm}"
                            :disabled="sendingForm"
                    >
                        <template v-if="!sendingForm">
                            {{ $t('project_settings.add_furniture_modal.add') }}
                        </template>
                        <template v-else>
                            {{ $t('project_settings.add_furniture_modal.adding') }}
                        </template>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {onMounted, ref, reactive, watch, inject, toRefs} from "vue";
import axios from "axios";
import ApiHelper from "@/helpsers/api.helper";
import authHelper from "@/helpsers/auth.helper";
import AlertHelper from "@/helpsers/alert.helper";
import router from "@/router";
import {useI18n} from "vue-i18n";
import {useField, useForm} from "vee-validate";
import * as yup from "yup";
import {useProjectSettingsStore} from "@/stores/user/architect/projectSettings.store";
import {storeToRefs} from "pinia";
import SelectSearch from "@/components/fields/select/selectSearch";
import "@/css/project-settings.scss";

export default {
    name: "AddFurnitureModal",
    components: {SelectSearch},
    setup(props, {emit}) {
        const isLoading = ref(false);
        const sendingForm = ref(false);
        const moreFurnituresLoaded = ref(false);
        const addFurnitureModal = ref();
        let alert = inject('alert');
        const {t} = useI18n();
        const projectSettingsStore = useProjectSettingsStore();
        let furnitures = ref();
        let selectedFurnituresIds = ref([]);
        let roomId = ref('');
        let roomSlug = ref('');
        let furnitureAmounts = ref({});

        onMounted(() => {
            addFurnitureModal.value = new A11yDialog(document.querySelector("#add-furniture-dialog"));
        });

        function fetchFurnitures() {
            isLoading.value = true;
            axios.get(ApiHelper.getFurnituresUrl(roomSlug.value), {
                headers: authHelper.authHeader(),
            }).then(result => {
                furnitures.value = result.data;
                isLoading.value = false;
            }).catch(error => {
                isLoading.value = false;
                let errorMessage = ApiHelper.getResponseMessage(error.response?.data?.code);
                AlertHelper.show(alert, errorMessage, '', 'error');
            });
        }

        function loadMoreFurnitures() {
            isLoading.value = true;
            axios.get(ApiHelper.getFurnituresUrl('', roomSlug.value), {
                headers: authHelper.authHeader(),
            }).then(result => {
                furnitures.value = furnitures.value.concat(result.data);
                moreFurnituresLoaded.value = true;
                isLoading.value = false;
            }).catch(error => {
                isLoading.value = false;
                let errorMessage = ApiHelper.getResponseMessage(error.response?.data?.code);
                AlertHelper.show(alert, errorMessage, '', 'error');
            });
        }

        function showModal(currentRoomId, currentRoomSlug, allRoomFurnituresData) {
            selectedFurnituresIds.value = [];
            furnitureAmounts.value = {};
            roomId.value = null;
            roomSlug.value = '';
            moreFurnituresLoaded.value = false;
            if (currentRoomId && allRoomFurnituresData) {
                roomId.value = currentRoomId;
                roomSlug.value = currentRoomSlug;
                addFurnitureModal.value.show();

                for (let roomFurnitureIndex in allRoomFurnituresData) {
                    if (!selectedFurnituresIds.value.includes(allRoomFurnituresData[roomFurnitureIndex].furnitureId)) {
                        selectedFurnituresIds.value.push(allRoomFurnituresData[roomFurnitureIndex].furnitureId);
                        furnitureAmounts.value[allRoomFurnituresData[roomFurnitureIndex].furnitureId] = allRoomFurnituresData[roomFurnitureIndex].amount
                    }
                }

                fetchFurnitures();
            }
        }

        function mapFurnituresForRequest() {
            let furnituresWithAmount = [];
            for (let selectedFurnitureIndex in selectedFurnituresIds.value) {
                let currentFurnitureId = selectedFurnituresIds.value[selectedFurnitureIndex]
                furnituresWithAmount.push({
                    'furnitureId': currentFurnitureId,
                    'amount': furnitureAmounts.value[currentFurnitureId] ?? 1
                })
            }

            return furnituresWithAmount;
        }

        const submitAddFurnitures = async () => {
            sendingForm.value = true;

            axios.put(ApiHelper.addFurnituresToRoomWithAmountUrl(router.currentRoute.value.params.id, roomId.value),
                {
                    furnitures: mapFurnituresForRequest(),
                },
                {
                    headers: authHelper.authHeader()
                }
            ).then(response => {
                sendingForm.value = false;
                emit('updateFurnituresAndParams', {
                    roomId: roomId.value,
                    data: response.data
                });
                selectedFurnituresIds.value = [];
                addFurnitureModal.value.hide();
                AlertHelper.show(alert, t('project_settings.add_furniture_modal.alert_success'));
            }).catch(error => {
                sendingForm.value = false;
                let errorMessage = ApiHelper.getResponseMessage(error.response?.data?.code);
                AlertHelper.show(alert, errorMessage, '', 'error');
            });
        };

        function removeItem(arr, item){
            return arr.filter(f => f !== item)
        }

        function toggleFurniture(id) {
            if (selectedFurnituresIds.value.includes(id)) {
                selectedFurnituresIds.value = removeItem(selectedFurnituresIds.value, id)
            } else {
                selectedFurnituresIds.value.push(id)
            }
        }

        function decrementAmount(furnitureId) {
            if (furnitureAmounts.value[furnitureId] && furnitureAmounts.value[furnitureId] > 1)
                furnitureAmounts.value[furnitureId] -= 1;
        }

        function incrementAmount(furnitureId) {
            if (furnitureAmounts.value[furnitureId])
                furnitureAmounts.value[furnitureId] += 1;
            else
                furnitureAmounts.value[furnitureId] = 2;
        }

        return {
            showModal,
            sendingForm,
            submitAddFurnitures,
            loadMoreFurnitures,
            furnitures,
            selectedFurnituresIds,
            toggleFurniture,
            ApiHelper,
            moreFurnituresLoaded,
            isLoading,
            decrementAmount,
            incrementAmount,
            furnitureAmounts
        }

    }

}
</script>

<style scoped lang="scss">
@use '@/css/variables';
@use '@/css/quiz';
@use '@/css/custom-quiz';

.dialog-content--furniture {
    width: 700px;
}

.furniture-item--selected {
    .furniture-item {
        &__top {
            background-color: #f4dbdb;
        }

        &__checked {
            background-color: #f4f3f3;
            border: 0;
            background-image: url("./../../../assets/icons/checkmark-dark.svg");
            background-position: center;
            background-repeat: no-repeat;
            background-size: 70%;
        }
    }
}

.furniture-item {
    &__bottom {
        text-align: center;
        display: flex;
        flex-direction: column;
    }
}

.furniture {
    &__list {
        justify-content: space-around;
        gap: 40px;
    }
}

.dialog__buttons {
    button {
        border: none;
        border-radius: 5px;
        font: normal normal 500 2.0rem/24px Montserrat;
        text-align: center;

        &:hover {
            background: #959595;
        }

        &.dialog__button--alt {
            background: map-get(variables.$colors, default-red);
            color: #fff;

            &:hover {
                background: #e35748;
            }
        }
    }
}

.control-number {
    margin-bottom: 10px;
}

.furniture-item {
    width: 170px;
    height: 170px;
}

.amount-input {
    text-align: center;
    padding: 0;
}

</style>